import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { useAuth } from '../../hooks/useAuth';
import { useEmailVerificationGate } from '../../hooks/useEmailVerificationGate';
import { useReviewPhotoUpload } from '../../hooks/useReviewPhotoUpload';
import { ContentContainer } from '../ContentContainer';
import { Button } from '../ui/Button';
import { ReviewList, VenueReviewForm, type VenueReviewFormData } from '../reviews';

interface VenueReviewsProps {
  venueId: string;
  venueName: string;
  isVenueManager?: boolean;
}

export const VenueReviews = ({ venueId, venueName, isVenueManager = false }: VenueReviewsProps) => {
  const { user } = useAuth();
  const { requireVerified, gateModal } = useEmailVerificationGate();
  const [showReviewForm, setShowReviewForm] = useState(false);
  const utils = trpc.useUtils();
  const {
    uploadPhotos,
    uploadStates: photoUploadStates,
    isUploading: isUploadingPhotos,
    reset: resetPhotoUploads,
  } = useReviewPhotoUpload(venueId);

  const createReviewMutation = trpc.venueReview.create.useMutation({
    onSuccess: () => {
      setShowReviewForm(false);
      resetPhotoUploads();
      // Invalidate the venue reviews list to refresh
      utils.venueReview.list.invalidate({ venueId });
    },
  });

  const handleSubmitReview = async (data: VenueReviewFormData) => {
    const imageIds = await uploadPhotos(data.photos);

    await createReviewMutation.mutateAsync({
      venueId,
      title: data.title,
      body: data.body,
      ratings: data.ratings,
      imageIds,
    });
  };

  const handleWriteReviewClick = () => {
    if (!requireVerified()) return;
    setShowReviewForm(true);
  };

  return (
    <ContentContainer>
      {gateModal}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Reviews</h2>
        {user && !showReviewForm && (
          <Button variant="primary" onClick={handleWriteReviewClick}>
            Write a Review
          </Button>
        )}
      </div>

      {/* Review Form */}
      {showReviewForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Write a Review for {venueName}
          </h3>
          {createReviewMutation.error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {createReviewMutation.error.message}
            </div>
          )}
          <VenueReviewForm
            onSubmit={handleSubmitReview}
            onCancel={() => setShowReviewForm(false)}
            isSubmitting={createReviewMutation.isPending || isUploadingPhotos}
            photoUploadStates={photoUploadStates}
          />
        </div>
      )}

      {/* Review List */}
      <ReviewList entityId={venueId} reviewType="venue" isVenueManager={isVenueManager} />
    </ContentContainer>
  );
};
